import React, { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, Send, Undo2, CheckCircle2, X as XIcon, PenTool, FileText, AlertTriangle, RefreshCw, History } from "lucide-react";
import SignaturePad from "@/components/SignaturePad";

/**
 * Blocco consenso privacy per anagrafica paziente / cartella clinica.
 * Props: { patient: { fiscal_code?, name, surname, birth?, phone?, email? }, minor? }
 */
export default function PatientConsentBlock({ patient, minor = false, legacy = null }) {
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // 'in_studio' | 'invite' | 'history' | null
  const [reissueConfirm, setReissueConfirm] = useState(false);

  const patientKey = React.useMemo(() => {
    if (patient?.fiscal_code) return `cf:${patient.fiscal_code.toUpperCase()}`;
    if (patient?.name && patient?.surname) {
      return `nb:${patient.name.trim().toLowerCase()}|${patient.surname.trim().toLowerCase()}|${(patient.birth || "").trim()}`;
    }
    return null;
  }, [patient]);

  const load = useCallback(async () => {
    if (!patientKey) { setLoading(false); return; }
    try {
      const { data } = await api.get("/medico/patient-consent-v2/current", { params: { patient_key: patientKey } });
      setCurrent(data);
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  }, [patientKey]);
  useEffect(() => { load(); }, [load]);

  const status = current?.status;
  const meta = current && current.status === "signed" ? current : null;

  const revoke = async () => {
    if (!meta) return;
    if (!window.confirm("Stai revocando il consenso online. Il paziente potrà ricevere referto cartaceo, ma non referto online finché non firma un nuovo consenso.")) return;
    try {
      await api.post("/medico/patient-consent-v2/revoke", { consent_id: meta.id, reason: "revoca da cartella" });
      toast.success("Consenso revocato");
      load();
    } catch (e) { toast.error(apiErr(e, "Errore revoca")); }
  };

  // Apri documento firmato in nuova tab via Blob URL (window.open("") con noopener
  // ritorna null su Chromium: il documento non si vedeva mai)
  const viewSignedDocument = (consentId) => {
    if (!consentId) return;
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    const tok = localStorage.getItem("pv_token") || "";
    fetch(`${backend}/api/medico/patient-consent-v2/${consentId}/document`, {
      headers: { Authorization: `Bearer ${tok}` },
    })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.text();
      })
      .then((html) => {
        const url = URL.createObjectURL(new Blob([html], { type: "text/html" }));
        const w = window.open(url, "_blank");
        if (!w) { toast.error("Popup bloccato: consenti popup e riprova"); return; }
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      })
      .catch((e) => toast.error("Errore apertura documento: " + e.message));
  };

  // Scarica PDF completo del consenso unico (privacy + dati + uso software, con firma)
  const downloadPdf = (consentId) => {
    if (!consentId) return;
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    const tok = localStorage.getItem("pv_token") || "";
    fetch(`${backend}/api/medico/patient-consent-v2/${consentId}/document.pdf`, {
      headers: { Authorization: `Bearer ${tok}` },
    })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.blob(); })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = "consenso_unico_paziente.pdf";
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 4000);
      })
      .catch((e) => toast.error("Errore download PDF: " + e.message));
  };

  const legacyAcquired = !meta && status !== "revoked" && !!legacy?.acquired;

  const statusMeta = {
    signed:  { label: "Acquisito", c: "bg-emerald-100 text-emerald-800", I: CheckCircle2 },
    missing: { label: "Non presente", c: "bg-amber-100 text-amber-800", I: AlertTriangle },
    revoked: { label: "Revocato", c: "bg-red-100 text-red-700", I: Undo2 },
  }[legacyAcquired ? "signed" : status] || { label: "—", c: "bg-slate-100 text-slate-500", I: ShieldCheck };
  const SI = statusMeta.I;

  if (!patientKey) return null;

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="consent-block">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex items-start gap-2">
          <ShieldCheck className="text-brand-navy" size={20} />
          <div>
            <div className="font-extrabold text-brand-navy leading-tight">Consenso unico paziente</div>
            <div className="text-[11px] text-slate-500">Privacy · trattamento dati · gestione digitale cartella clinica — valido fino a revoca</div>
          </div>
        </div>
        <span className={`${statusMeta.c} px-2 py-0.5 rounded-full text-[11px] font-bold inline-flex items-center gap-1`} data-testid="consent-status">
          <SI size={12} /> {statusMeta.label}
        </span>
      </div>

      {meta && (
        <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-900 space-y-0.5" data-testid="consent-signed-summary">
          <div className="font-bold" data-testid="consent-signed-date">Consenso acquisito il {meta.signed_at ? new Date(meta.signed_at).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" }) : "—"}</div>
          <div>Versione documento: <b>{meta.consent_version}</b> · Canale: {meta.channel} {meta.minor && <span className="ml-2 bg-amber-100 text-amber-800 text-[10px] font-bold px-2 py-0.5 rounded-full">MINORENNE</span>}</div>
          <div className="flex gap-3 mt-1">
            {["privacy", "referto_online", "ia_audio"].map((k) => (
              <span key={k} className={`text-[10px] font-bold px-2 py-0.5 rounded ${meta.sections?.[k] ? "bg-emerald-100 text-emerald-800 border border-emerald-300" : "bg-slate-50 text-slate-400 border border-slate-200"}`}>
                {k === "privacy" ? "Informativa" : k === "referto_online" ? "Referto online" : "IA/audio 15gg"}
              </span>
            ))}
          </div>
        </div>
      )}

      {legacyAcquired && (
        <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-900" data-testid="consent-legacy-summary">
          <div className="font-bold">Consenso acquisito{legacy?.acquiredAt ? ` il ${new Date(legacy.acquiredAt).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" })}` : ""}</div>
          <div className="mt-0.5 text-emerald-800">Registrazione storica senza firma digitale: dato conservato. Raccogli la firma per generare anteprima e PDF del documento.</div>
        </div>
      )}

      {!loading && (
        <div className="mt-3 flex flex-wrap gap-2">
          {/* Se NON esiste firma valida → flusso normale (raccogli in studio o invia link) */}
          {!meta && (
            <>
              <button onClick={() => setModal("in_studio")} data-testid="consent-btn-in-studio" className="text-xs font-bold bg-brand-navy hover:bg-brand-navyDark text-white px-3 py-1.5 rounded flex items-center gap-1"><PenTool size={12} /> Raccogli in studio</button>
              <button onClick={() => setModal("invite")} data-testid="consent-btn-invite" className="text-xs font-bold bg-blue-50 hover:bg-blue-100 text-blue-800 border border-blue-200 px-3 py-1.5 rounded flex items-center gap-1"><Send size={12} /> Invia link SMS/email</button>
            </>
          )}
          {/* Se già firmato → NO invio automatico, mostra "Visualizza documento" + "Aggiorna consenso" esplicito */}
          {meta && (
            <>
              <button onClick={() => viewSignedDocument(meta.id)} data-testid="consent-btn-view-document" className="text-xs font-bold bg-brand-navy hover:bg-brand-navyDark text-white px-3 py-1.5 rounded flex items-center gap-1"><FileText size={12} /> Visualizza anteprima</button>
              <button onClick={() => downloadPdf(meta.id)} data-testid="consent-btn-download-pdf" className="text-xs font-bold bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded flex items-center gap-1"><FileText size={12} /> Scarica PDF</button>
              <button onClick={() => setReissueConfirm(true)} data-testid="consent-btn-reissue" className="text-xs font-bold bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-200 px-3 py-1.5 rounded flex items-center gap-1"><RefreshCw size={12} /> Aggiorna consenso</button>
              <button onClick={revoke} data-testid="consent-btn-revoke" className="text-xs font-bold bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 px-3 py-1.5 rounded flex items-center gap-1"><Undo2 size={12} /> Revoca</button>
            </>
          )}
          <button onClick={() => setModal("history")} data-testid="consent-btn-history" className="text-xs font-bold bg-slate-100 hover:bg-slate-200 text-slate-700 px-3 py-1.5 rounded flex items-center gap-1"><History size={12} /> Storico</button>
        </div>
      )}

      {modal === "in_studio" && (
        <InStudioModal patient={patient} minor={minor || meta?.minor} onClose={() => setModal(null)} onDone={() => { setModal(null); load(); }} />
      )}
      {modal === "invite" && (
        <InviteModal patient={patient} minor={minor || meta?.minor} forceReissue={false} onClose={() => setModal(null)} onDone={() => setModal(null)} />
      )}
      {modal === "history" && (
        <HistoryModal patientKey={patientKey} onClose={() => setModal(null)} onView={viewSignedDocument} />
      )}
      {reissueConfirm && (
        <ReissueConfirmModal
          onCancel={() => setReissueConfirm(false)}
          onConfirm={() => { setReissueConfirm(false); setModal("invite"); }}
        />
      )}
    </div>
  );
}

function InStudioModal({ patient, minor: minorInit, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const [a, setA] = useState(false); const [b, setB] = useState(false); const [c, setC] = useState(false);
  const [sig, setSig] = useState(null);
  const [minor, setMinor] = useState(!!minorInit);
  const [gName, setGName] = useState(""); const [gSur, setGSur] = useState("");
  const [gFisc, setGFisc] = useState(""); const [gRap, setGRap] = useState("genitore");

  const submit = async () => {
    if (!(a && b && c)) { toast.error("Accetta tutte e 3 le sezioni"); return; }
    if (!sig) { toast.error("Firma obbligatoria"); return; }
    if (minor && (!gName.trim() || !gSur.trim())) { toast.error("Nome/cognome tutore obbligatori"); return; }
    setBusy(true);
    try {
      const b64 = sig.split(",")[1];
      await api.post("/medico/patient-consent-v2/in-studio", {
        fiscal_code: patient?.fiscal_code, name: patient.name, surname: patient.surname,
        birth: patient?.birth, phone: patient?.phone, email: patient?.email,
        minor, guardian_name: minor ? gName : undefined,
        guardian_surname: minor ? gSur : undefined,
        guardian_fiscal: minor ? gFisc : undefined,
        guardian_rapporto: minor ? gRap : undefined,
        section_privacy: true, section_referto_online: true, section_ia_audio: true,
        signature_png_base64: b64,
      });
      toast.success("Consenso registrato");
      onDone();
    } catch (e) { toast.error(apiErr(e, "Errore firma")); }
    finally { setBusy(false); }
  };

  return (
    <Modal onClose={onClose} title="Consenso privacy in studio" testid="consent-instudio-modal">
      <div className="text-xs text-slate-500 mb-2">Paziente: <b>{patient.name} {patient.surname}</b></div>
      <SectionsChecks a={a} b={b} c={c} setA={setA} setB={setB} setC={setC} />
      <label className="flex items-center gap-2 text-xs text-slate-700 mt-2">
        <input type="checkbox" checked={minor} onChange={(e) => setMinor(e.target.checked)} data-testid="consent-instudio-minor" /> Paziente minorenne
      </label>
      {minor && (
        <div className="border border-amber-200 bg-amber-50 rounded p-2 mt-2 space-y-1">
          <div className="grid grid-cols-2 gap-2">
            <input value={gName} onChange={(e) => setGName(e.target.value)} placeholder="Nome tutore" data-testid="consent-instudio-gname" className="px-2 py-1 rounded border border-slate-300 text-sm bg-white" />
            <input value={gSur} onChange={(e) => setGSur(e.target.value)} placeholder="Cognome tutore" className="px-2 py-1 rounded border border-slate-300 text-sm bg-white" />
          </div>
          <input value={gFisc} onChange={(e) => setGFisc(e.target.value)} placeholder="CF tutore (opzionale)" className="w-full px-2 py-1 rounded border border-slate-300 text-sm bg-white" />
          <select value={gRap} onChange={(e) => setGRap(e.target.value)} className="w-full px-2 py-1 rounded border border-slate-300 text-sm bg-white">
            <option value="genitore">Genitore</option>
            <option value="tutore_legale">Tutore legale</option>
          </select>
        </div>
      )}
      <div className="mt-3">
        <div className="text-xs font-bold text-slate-700 mb-1">Firma paziente / tutore</div>
        <SignaturePad onChange={setSig} dataTestid="consent-instudio-signature" />
      </div>
      <button onClick={submit} disabled={busy} data-testid="consent-instudio-submit" className="mt-3 w-full bg-brand-navy hover:bg-brand-navyDark text-white font-bold rounded px-4 py-2 disabled:opacity-60">Registra consenso</button>
    </Modal>
  );
}

function InviteModal({ patient, minor: minorInit, forceReissue = false, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const [channel, setChannel] = useState(
    patient?.phone && patient?.email ? "both" : patient?.phone ? "sms" : "email"
  );
  const [minor, setMinor] = useState(!!minorInit);
  const [gName, setGName] = useState(""); const [gSur, setGSur] = useState("");
  const [gRap, setGRap] = useState("genitore");
  const noContact = !patient?.phone && !patient?.email;

  const submit = async () => {
    if (noContact) { toast.error("Il paziente non ha telefono né email in anagrafica"); return; }
    if (minor && (!gName.trim() || !gSur.trim())) { toast.error("Nome/cognome tutore obbligatori"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/medico/patient-consent-v2/invite", {
        fiscal_code: patient?.fiscal_code, name: patient.name, surname: patient.surname,
        birth: patient?.birth, phone: patient?.phone, email: patient?.email,
        minor, guardian_name: minor ? gName : undefined,
        guardian_surname: minor ? gSur : undefined,
        guardian_rapporto: minor ? gRap : undefined,
        channel,
        force_reissue: forceReissue,
      });
      toast.success(`Link inviato via ${channel === "both" ? "SMS+email" : channel}. ${data.delivered_sms ? "SMS ok. " : ""}${data.delivered_email ? "Email ok." : ""}`);
      onDone();
    } catch (e) { toast.error(apiErr(e, "Errore invio invito")); }
    finally { setBusy(false); }
  };

  return (
    <Modal onClose={onClose} title="Invia link consenso" testid="consent-invite-modal">
      <div className="text-xs text-slate-500 mb-2">Paziente: <b>{patient.name} {patient.surname}</b></div>
      {noContact && <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-800">Manca telefono e email in anagrafica: aggiungili prima di inviare l&apos;invito.</div>}
      {!noContact && (
        <div className="space-y-2">
          <div className="text-xs text-slate-600">
            {patient?.phone && <div>Telefono: <b>{patient.phone}</b></div>}
            {patient?.email && <div>Email: <b>{patient.email}</b></div>}
          </div>
          <select value={channel} onChange={(e) => setChannel(e.target.value)} data-testid="consent-invite-channel" className="w-full px-2 py-1.5 rounded border border-slate-300 text-sm bg-white">
            {patient?.phone && patient?.email && <option value="both">SMS + Email</option>}
            {patient?.phone && <option value="sms">Solo SMS</option>}
            {patient?.email && <option value="email">Solo Email</option>}
          </select>
          <label className="flex items-center gap-2 text-xs text-slate-700">
            <input type="checkbox" checked={minor} onChange={(e) => setMinor(e.target.checked)} /> Paziente minorenne (firma tutore)
          </label>
          {minor && (
            <div className="border border-amber-200 bg-amber-50 rounded p-2 space-y-1">
              <div className="grid grid-cols-2 gap-2">
                <input value={gName} onChange={(e) => setGName(e.target.value)} placeholder="Nome tutore" className="px-2 py-1 rounded border border-slate-300 text-sm bg-white" />
                <input value={gSur} onChange={(e) => setGSur(e.target.value)} placeholder="Cognome tutore" className="px-2 py-1 rounded border border-slate-300 text-sm bg-white" />
              </div>
              <select value={gRap} onChange={(e) => setGRap(e.target.value)} className="w-full px-2 py-1 rounded border border-slate-300 text-sm bg-white">
                <option value="genitore">Genitore</option>
                <option value="tutore_legale">Tutore legale</option>
              </select>
            </div>
          )}
          <button onClick={submit} disabled={busy || noContact} data-testid="consent-invite-submit" className="w-full bg-brand-navy hover:bg-brand-navyDark text-white font-bold rounded px-4 py-2 disabled:opacity-60">Invia link</button>
        </div>
      )}
    </Modal>
  );
}

function ReissueConfirmModal({ onCancel, onConfirm }) {
  return (
    <Modal onClose={onCancel} title="Aggiornare il consenso paziente?" testid="consent-reissue-confirm">
      <div className="text-sm text-slate-700 space-y-2">
        <p>Stai per richiedere una <b>nuova firma</b> del consenso privacy al paziente.</p>
        <p>Verrà inviato un nuovo link SMS/email con codice OTP. Il consenso precedente rimane comunque nello storico documentale.</p>
        <p className="text-xs text-slate-500">Confermi di voler procedere?</p>
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <button onClick={onCancel} data-testid="consent-reissue-cancel" className="text-xs font-bold px-3 py-1.5 rounded text-slate-600 hover:bg-slate-100">Annulla</button>
        <button onClick={onConfirm} data-testid="consent-reissue-confirm" className="text-xs font-bold px-3 py-1.5 rounded text-white bg-amber-600 hover:bg-amber-700">Sì, aggiorna consenso</button>
      </div>
    </Modal>
  );
}

function HistoryModal({ patientKey, onClose, onView }) {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let mounted = true;
    api.get("/medico/patient-consent-v2/history", { params: { patient_key: patientKey } })
      .then((r) => { if (mounted) setRows(r.data.items || []); })
      .catch(() => { if (mounted) setRows([]); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [patientKey]);
  return (
    <Modal onClose={onClose} title="Storico consensi paziente" testid="consent-history-modal">
      {loading && <div className="text-sm text-slate-500 py-3" data-testid="consent-history-loading">Caricamento…</div>}
      {!loading && rows?.length === 0 && <div className="text-sm text-slate-500 py-3" data-testid="consent-history-empty">Nessun consenso registrato.</div>}
      {!loading && rows?.length > 0 && (
        <ul className="divide-y divide-slate-200" data-testid="consent-history-list">
          {rows.map((r) => (
            <li key={r.id} className="py-2 flex items-start justify-between gap-2 text-xs" data-testid={`consent-history-item-${r.id}`}>
              <div>
                <div className="font-bold text-slate-800">
                  {r.status === "signed" && !r.revoked ? "Firmato" : r.revoked ? "Revocato" : (r.status || "—")}
                  {" · v"}{r.consent_version}
                </div>
                <div className="text-slate-500">
                  {r.signed_at ? `Firmato ${new Date(r.signed_at).toLocaleString("it-IT")}` : `Creato ${new Date(r.created_at).toLocaleString("it-IT")}`}
                  {" · Canale "}{r.channel}
                  {r.otp_verified ? " · OTP verificato" : ""}
                </div>
                {r.revoked && r.revoked_at && (
                  <div className="text-red-600">Revocato il {new Date(r.revoked_at).toLocaleString("it-IT")}</div>
                )}
              </div>
              {r.status === "signed" && (
                <button
                  onClick={() => onView(r.id)}
                  data-testid={`consent-history-view-${r.id}`}
                  className="text-[11px] font-bold text-brand-navy hover:underline whitespace-nowrap"
                >
                  <FileText size={10} className="inline mr-1" />
                  Documento
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}

function Modal({ onClose, title, children, testid }) {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto" data-testid={testid}>
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full my-4">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <div className="font-extrabold text-brand-navy">{title}</div>
          <button onClick={onClose}><XIcon size={18} /></button>
        </div>
        <div className="px-5 py-4">{children}</div>
      </div>
    </div>
  );
}

function SectionsChecks({ a, b, c, setA, setB, setC }) {
  const items = [
    {k: "A", val: a, set: setA, testid: "sec-privacy",
     text: "Informativa privacy compresa e accettata."},
    {k: "B", val: b, set: setB, testid: "sec-referto",
     text: "Referto online via email/SMS con link PDF."},
    {k: "C", val: c, set: setC, testid: "sec-ia",
     text: "Uso IA/audio con retention 15 giorni."},
  ];
  return (
    <div className="space-y-1.5 mt-2">
      {items.map((s) => (
        <label key={s.k} className="flex items-start gap-2 cursor-pointer text-sm text-slate-800 p-2 rounded border border-slate-200 hover:border-brand-azure">
          <input type="checkbox" checked={s.val} onChange={(e) => s.set(e.target.checked)} data-testid={s.testid} className="mt-0.5" />
          <span><b className="text-slate-500 text-[10px] uppercase mr-1">{s.k}.</b> {s.text}</span>
        </label>
      ))}
    </div>
  );
}
